/**
 * FavoritesProvider — single source of truth for cloud + local + unified favorites.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { EdgeFnError } from '@/lib/edgeFn';
import {
  createFavorite,
  deleteFavorite,
  updateFavorite,
} from '@/lib/favoritesApi';
import { resolveTranslation } from '@/contexts/LanguageContext';
import { getAppLanguage } from '@/lib/appLanguage';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/contexts/AuthContext';
import { isMergeComplete, hasMergeMarker, markMergeComplete, runPhase3Merge } from '@/lib/favoritesMerge';
import { FreeLimitError, getEffectiveFavoriteLimit } from '@/lib/proLimits';
import { isUnifiedFavoritesEnabled } from '@/lib/favoritesUnifiedFlag';
import { toast } from '@/hooks/use-toast';
import {
  buildUnifiedSambandList,
  filterCloudSambandFavorites,
  normalizeLegacyRouteToFerryFavorite,
  shouldShowMyFerriesSection,
} from '@/lib/ferryFavoriteResolution';
import { runPhase2Migration } from '@/lib/favoritesMigration';
import {
  CLOUD_CACHE_KEY,
  CLOUD_SYNC_EVENT,
  LEGACY_CLOUD_KEY,
  LOCAL_SYNC_EVENT,
  LOCAL_V2_KEY,
  PENDING_KEY,
  PENDING_SYNC_EVENT,
  type FavoriteRoute,
  type FerryFavorite,
  type MutateResponse,
  type PendingFavoriteEntry,
  countDistinctFavoriteSlugs,
  dequeuePending,
  enqueuePending,
  isDeliberateFavoriteServerRejection,
  isOfflineFavoriteWriteError,
  normalizeSambandSlug,
  pendingEntryDisplayName,
  prunePendingAlreadyInCloud,
  readPendingQueue,
  readLocalRoutes,
  clearCloudCache,
  isCloudMigrated,
  isLocalDisplayArchived,
  maybeSetLocalDisplayArchived,
  setLocalDisplayArchived,
  syncCloudFavoritesFromStorage,
  syncLocalRoutesFromStorage,
  syncPendingFromStorage,
  sortPendingFifo,
  waitForAnonUserId,
  writeCloudCache,
  readCloudCacheOwner,
  ANON_CACHE_OWNER,
  writeLocalRoutes,
  fetchAndMergeCloudFavorites,
} from '@/lib/favoritesStore';

export type ToggleSambandMeta = {
  from?: string;
  to?: string;
  customName?: string;
  isPro?: boolean;
  /** Flag-off write target; ignored when unified flag is on. Defaults to `'cloud'`. */
  legacyWrite?: 'local' | 'cloud';
};

export type UnifiedFavoritesCounts = {
  local: number;
  cloud: number;
  merged: number;
};

export type FavoritesContextValue = {
  cloudFavorites: FerryFavorite[];
  localRoutes: FavoriteRoute[];
  mergedList: FerryFavorite[];
  isLoading: boolean;
  error: string | null;
  counts: UnifiedFavoritesCounts;
  showMyFerriesSection: boolean;
  refresh: () => Promise<void>;
  isFavorite: (slug: string) => boolean;
  toggleSamband: (slug: string, meta?: ToggleSambandMeta) => Promise<boolean>;
  removeSamband: (slug: string, meta?: ToggleSambandMeta) => Promise<void>;
  cloudIsFavorite: (slug: string) => boolean;
  addCloudFavorite: (
    slug: string,
    customName?: string,
    options?: { isPro?: boolean; from?: string; to?: string },
  ) => Promise<void>;
  removeCloudFavorite: (slugOrId: string) => Promise<void>;
  updateCloudFavorite: (
    id: string,
    updates: Partial<Omit<FerryFavorite, 'id' | 'samband_slug' | 'created_at' | 'updated_at'>>,
  ) => Promise<void>;
  toggleCloudFavorite: (
    slug: string,
    options?: { isPro?: boolean; from?: string; to?: string },
  ) => Promise<void>;
  refreshCloud: () => Promise<void>;
  localIsFavorite: (slug: string) => boolean;
  addLocalRoute: (slug: string, from: string, to: string) => void;
  removeLocalRoute: (slug: string) => void;
  toggleLocalRoute: (slug: string, from: string, to: string) => void;
};

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

function findLocalRoute(
  routes: FavoriteRoute[],
  normalizedSlug: string,
): FavoriteRoute | undefined {
  return routes.find((route, index) => {
    if (normalizeSambandSlug(route.slug) === normalizedSlug || route.slug === normalizedSlug) {
      return true;
    }
    const resolved = normalizeLegacyRouteToFerryFavorite(route, index);
    return resolved.samband_slug === normalizedSlug;
  });
}

function hasLocalSamband(routes: FavoriteRoute[], normalizedSlug: string): boolean {
  return findLocalRoute(routes, normalizedSlug) !== undefined;
}

function findCloudFavoriteBySlug(
  favorites: FerryFavorite[],
  normalizedSlug: string,
): FerryFavorite | undefined {
  return favorites.find((favorite) => favorite.samband_slug === normalizedSlug);
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const unifiedEnabled = isUnifiedFavoritesEnabled();
  const { session, loading: authLoading } = useAuth();
  const [cloudFavorites, setCloudFavorites] = useState<FerryFavorite[]>(() => syncCloudFavoritesFromStorage());
  const [localRoutes, setLocalRoutes] = useState<FavoriteRoute[]>(() => syncLocalRoutesFromStorage());
  const [pendingQueue, setPendingQueue] = useState<PendingFavoriteEntry[]>(() => syncPendingFromStorage());
  const [displayArchived, setDisplayArchived] = useState(() => isLocalDisplayArchived());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const mergeInFlightRef = useRef(false);
  const mergeAuthoritativeRef = useRef(false);
  const syncPendingInFlightRef = useRef(false);
  const cloudFavoritesRef = useRef(cloudFavorites);
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  /** Hvem som er logget inn nå, for å merke hurtiglageret med riktig eier. */
  const cacheOwnerRef = useRef<string>(ANON_CACHE_OWNER);
  cacheOwnerRef.current = session?.user?.id ?? ANON_CACHE_OWNER;

  useEffect(() => {
    cloudFavoritesRef.current = cloudFavorites;
  }, [cloudFavorites]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const applyCloudFavorites = useCallback((next: FerryFavorite[]) => {
    if (!mountedRef.current) return;
    setCloudFavorites(next);
    writeCloudCache(next, cacheOwnerRef.current);
  }, []);

  const markDisplayArchived = useCallback(() => {
    setLocalDisplayArchived();
    if (mountedRef.current) setDisplayArchived(true);
  }, []);

  const notifySuccessfulCloudApply = useCallback(async (serverFavorites: FerryFavorite[]) => {
    const anonUserId = await waitForAnonUserId();
    maybeSetLocalDisplayArchived(
      anonUserId,
      session?.user?.id ?? null,
      isMergeComplete,
      hasMergeMarker(),
      serverFavorites,
    );
    if (isLocalDisplayArchived()) {
      markDisplayArchived();
    }
  }, [session, markDisplayArchived]);

  const applyLocalRoutes = useCallback((next: FavoriteRoute[]) => {
    if (!mountedRef.current) return;
    setLocalRoutes(next);
    writeLocalRoutes(next);
  }, []);

  const syncPendingFavorites = useCallback(async () => {
    if (syncPendingInFlightRef.current) return;
    if (readPendingQueue().length === 0) return;

    syncPendingInFlightRef.current = true;
    try {
      const anonUserId = await waitForAnonUserId();
      let currentCloud = cloudFavoritesRef.current;

      const pruned = prunePendingAlreadyInCloud(currentCloud.map((f) => f.samband_slug));
      if (mountedRef.current) setPendingQueue(pruned);

      const fifo = sortPendingFifo(readPendingQueue());
      for (const entry of fifo) {
        if (!readPendingQueue().some((item) => item.samband_slug === entry.samband_slug)) {
          continue;
        }

        try {
          const data = await createFavorite(
            {
              samband_slug: entry.samband_slug,
              custom_name: entry.custom_name ?? null,
              position: currentCloud.length,
            },
            anonUserId,
          );

          if (data?.favorite) {
            // Cloud-first handoff: mergedList dedupes cloud over pending — no flash/dup badge.
            currentCloud = [...currentCloud.filter((f) => f.samband_slug !== entry.samband_slug), data.favorite]
              .sort((a, b) => a.position - b.position);
            applyCloudFavorites(currentCloud);
            cloudFavoritesRef.current = currentCloud;
            const nextPending = dequeuePending(entry.samband_slug);
            if (mountedRef.current) setPendingQueue(nextPending);
            trackEvent('ferry_favorite_synced', { route: entry.samband_slug });
          }
        } catch (err) {
          console.error('[FavoritesProvider] pending sync failed', entry.samband_slug, err);
          const navn = pendingEntryDisplayName(entry);

          if (err instanceof EdgeFnError && err.code === 'LIMIT_REACHED') {
            const nextPending = dequeuePending(entry.samband_slug);
            if (mountedRef.current) setPendingQueue(nextPending);
            toast({
              title: `Kunne ikke synkronisere '${navn}' – du har nådd grensen. Oppgrader til Pro for flere.`,
              variant: 'destructive',
            });
            continue;
          }

          if (err instanceof EdgeFnError && isDeliberateFavoriteServerRejection(err)) {
            const nextPending = dequeuePending(entry.samband_slug);
            if (mountedRef.current) setPendingQueue(nextPending);
            toast({
              title: `Kunne ikke synkronisere '${navn}'.`,
              variant: 'destructive',
            });
            continue;
          }

          if (isOfflineFavoriteWriteError(err)) {
            break;
          }

          break;
        }
      }
    } finally {
      syncPendingInFlightRef.current = false;
    }
  }, [applyCloudFavorites]);

  const fetchCloud = useCallback(async (opts?: { allowApply?: boolean }): Promise<FerryFavorite[]> => {
    const anonUserId = await waitForAnonUserId();

    try {
      const { cloudFavorites: fetched, merged, fetchError } = await fetchAndMergeCloudFavorites(anonUserId);
      if (opts?.allowApply === false) {
        // explicit suppress (mount when merge already authoritative at call time)
      } else if (opts?.allowApply === true) {
        applyCloudFavorites(merged);
        if (!fetchError) {
          void notifySuccessfulCloudApply(fetched);
          await syncPendingFavorites();
        }
      } else if (!mergeAuthoritativeRef.current) {
        applyCloudFavorites(merged);
        if (!fetchError) {
          void notifySuccessfulCloudApply(fetched);
          await syncPendingFavorites();
        }
      }
      if (mountedRef.current) {
        setError(fetchError?.message ?? null);
      }
      return fetched;
    } catch (err) {
      if (!mountedRef.current) return [];
      console.warn('[FavoritesProvider] fetch failed, using local favorites', err);
      const fallback = syncCloudFavoritesFromStorage(cacheOwnerRef.current);
      applyCloudFavorites(fallback);
      setError(err instanceof Error ? err.message : resolveTranslation(getAppLanguage(), 'favorites_fetch_failed'));
      return [];
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [applyCloudFavorites, notifySuccessfulCloudApply, syncPendingFavorites]);

  const refreshCloud = useCallback(async () => {
    await fetchCloud({ allowApply: true });
    mergeAuthoritativeRef.current = false;
    const anonUserId = await waitForAnonUserId();
    const result = await runPhase2Migration(anonUserId);
    if (result.status === 'confirmed') {
      const { cloudFavorites: serverFavorites, merged, fetchError } = await fetchAndMergeCloudFavorites(anonUserId);
      applyCloudFavorites(merged);
      if (!fetchError) {
        void notifySuccessfulCloudApply(serverFavorites);
        await syncPendingFavorites();
      }
    }
  }, [applyCloudFavorites, fetchCloud, notifySuccessfulCloudApply, syncPendingFavorites]);

  useEffect(() => {
    if (authLoading) return;

    const userId = session?.user?.id ?? null;
    const prevUserId = prevUserIdRef.current;

    // Hurtiglager som tilhører en annen konto enn den som er logget inn nå (for
    // eksempel etter at innloggingen har gått ut), tømmes før noe vises.
    const cacheOwner = readCloudCacheOwner();
    const currentOwner = userId ?? ANON_CACHE_OWNER;
    if (cacheOwner !== null && cacheOwner !== ANON_CACHE_OWNER && cacheOwner !== currentOwner) {
      clearCloudCache();
      if (mountedRef.current) setCloudFavorites([]);
    } else if (userId && cacheOwner === userId && mountedRef.current) {
      // Samme konto som sist: vis hurtiglageret med en gang, mens skyen hentes.
      setCloudFavorites((prev) => (prev.length > 0 ? prev : syncCloudFavoritesFromStorage(userId)));
    }

    if (prevUserId != null && userId === null) {
      clearCloudCache();
      if (isCloudMigrated()) {
        markDisplayArchived();
      }
      if (mountedRef.current) {
        setCloudFavorites([]);
        mergeAuthoritativeRef.current = false;
      }
      void fetchCloud({ allowApply: true });
    }

    prevUserIdRef.current = userId;
  }, [session, authLoading, fetchCloud, markDisplayArchived]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      await fetchCloud(
        mergeAuthoritativeRef.current ? { allowApply: false } : {},
      );
      if (cancelled) return;

      const anonUserId = await waitForAnonUserId();
      const result = await runPhase2Migration(anonUserId);
      if (cancelled || !mountedRef.current) return;

      if (result.status === 'confirmed' && !mergeAuthoritativeRef.current) {
        const { cloudFavorites: serverFavorites, merged, fetchError } = await fetchAndMergeCloudFavorites(anonUserId);
        if (cancelled || !mountedRef.current) return;
        applyCloudFavorites(merged);
        if (!fetchError) {
          void notifySuccessfulCloudApply(serverFavorites);
          await syncPendingFavorites();
        }
      }
      setError(null);

      if (
        !cancelled
        && mountedRef.current
        && typeof navigator !== 'undefined'
        && navigator.onLine
        && readPendingQueue().length > 0
      ) {
        await syncPendingFavorites();
      }
    };

    void run();
    return () => { cancelled = true; };
  }, [applyCloudFavorites, fetchCloud, notifySuccessfulCloudApply, syncPendingFavorites]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const onOnline = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (readPendingQueue().length > 0) {
          void syncPendingFavorites();
        }
      }, 250);
    };

    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      if (timer) clearTimeout(timer);
    };
  }, [syncPendingFavorites]);

  useEffect(() => {
    const userId = session?.user?.id;
    const accessToken = session?.access_token;
    if (!userId || !accessToken) {
      mergeAuthoritativeRef.current = false;
      return;
    }

    let cancelled = false;

    void (async () => {
      const anonUserId = await waitForAnonUserId();
      if (cancelled || !mountedRef.current) return;
      if (isMergeComplete(anonUserId, userId)) return;
      if (mergeInFlightRef.current) return;

      mergeInFlightRef.current = true;
      try {
        const result = await runPhase3Merge(session, anonUserId);
        if (cancelled || !mountedRef.current) return;

        if (result.ok && Array.isArray(result.data?.favorites)) {
          markMergeComplete(anonUserId, userId);
          mergeAuthoritativeRef.current = true;
          applyCloudFavorites(result.data.favorites);
          void notifySuccessfulCloudApply(result.data.favorites);
        }
      } finally {
        mergeInFlightRef.current = false;
      }
    })();

    return () => { cancelled = true; };
  }, [session, applyCloudFavorites, notifySuccessfulCloudApply]);

  useEffect(() => {
    const syncCloud = () => {
      setCloudFavorites(syncCloudFavoritesFromStorage(cacheOwnerRef.current));
    };
    const syncLocal = () => {
      setLocalRoutes(syncLocalRoutesFromStorage());
    };
    const syncPending = () => {
      setPendingQueue(syncPendingFromStorage());
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === CLOUD_CACHE_KEY || e.key === LEGACY_CLOUD_KEY) syncCloud();
      if (e.key === LOCAL_V2_KEY) syncLocal();
      if (e.key === PENDING_KEY) syncPending();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(CLOUD_SYNC_EVENT, syncCloud);
    window.addEventListener(LOCAL_SYNC_EVENT, syncLocal);
    window.addEventListener(PENDING_SYNC_EVENT, syncPending);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(CLOUD_SYNC_EVENT, syncCloud);
      window.removeEventListener(LOCAL_SYNC_EVENT, syncLocal);
      window.removeEventListener(PENDING_SYNC_EVENT, syncPending);
    };
  }, []);

  const cloudSambandFavorites = useMemo(
    () => filterCloudSambandFavorites(cloudFavorites),
    [cloudFavorites],
  );

  const localRoutesForDisplay = useMemo(
    () => (displayArchived ? [] : localRoutes),
    [displayArchived, localRoutes],
  );

  const mergedList = useMemo(
    () => buildUnifiedSambandList(
      cloudFavorites,
      localRoutesForDisplay,
      { isCloudLoading: isLoading },
      pendingQueue,
    ),
    [cloudFavorites, localRoutesForDisplay, isLoading, pendingQueue],
  );

  const counts = useMemo<UnifiedFavoritesCounts>(() => ({
    local: localRoutes.length,
    cloud: cloudSambandFavorites.length,
    merged: mergedList.length,
  }), [localRoutes.length, cloudSambandFavorites.length, mergedList.length]);

  const showMyFerriesSection = useMemo(
    () => shouldShowMyFerriesSection(cloudFavorites, localRoutesForDisplay, isLoading, pendingQueue),
    [cloudFavorites, localRoutesForDisplay, isLoading, pendingQueue],
  );

  const cloudIsFavorite = useCallback(
    (slug: string) => cloudFavorites.some((f) => f.samband_slug === slug),
    [cloudFavorites],
  );

  const localIsFavorite = useCallback(
    (slug: string) => localRoutes.some((f) => f.slug === slug),
    [localRoutes],
  );

  const isFavorite = useCallback(
    (slug: string) => {
      const normalized = normalizeSambandSlug(slug);
      if (normalized.length === 0) return false;
      return mergedList.some((favorite) => favorite.samband_slug === normalized);
    },
    [mergedList],
  );

  const addCloudFavorite = useCallback(async (
    slug: string,
    customName?: string,
    options?: { isPro?: boolean; from?: string; to?: string },
  ) => {
    const anonUserId = await waitForAnonUserId();
    const normalizedSlug = normalizeSambandSlug(slug);
    const alreadyExists = cloudFavorites.some((f) => f.samband_slug === normalizedSlug);
    const alreadyPending = pendingQueue.some((p) => p.samband_slug === normalizedSlug);
    const favoriteLimit = getEffectiveFavoriteLimit();
    const effectiveCount = countDistinctFavoriteSlugs(
      cloudSambandFavorites.map((f) => f.samband_slug),
      pendingQueue,
    );
    if (!alreadyExists && !alreadyPending && !options?.isPro && effectiveCount >= favoriteLimit) {
      trackEvent('pro_gate_blocked', { feature: 'favorites_limit', current: effectiveCount });
      throw new FreeLimitError(`Free plan allows max ${favoriteLimit} favorites`);
    }
    try {
      const data = await createFavorite(
        {
          samband_slug: slug,
          custom_name: customName ?? null,
          position: cloudFavorites.length,
        },
        anonUserId,
      );
      if (data?.favorite) {
        const next = [...cloudFavorites.filter((f) => f.samband_slug !== slug), data.favorite]
          .sort((a, b) => a.position - b.position);
        applyCloudFavorites(next);
        trackEvent('ferry_favorite_added', { route: slug });
      }
    } catch (err) {
      console.error('[FavoritesProvider] add failed', err);
      if (err instanceof EdgeFnError && err.code === 'LIMIT_REACHED') {
        throw new FreeLimitError(`Free plan allows max ${getEffectiveFavoriteLimit()} favorites`);
      }
      if (isOfflineFavoriteWriteError(err)) {
        const nextPending = enqueuePending({
          samband_slug: normalizedSlug,
          custom_name: customName ?? null,
          from: options?.from,
          to: options?.to,
        });
        setPendingQueue(nextPending);
        toast({ title: 'Lagt til – synkroniseres når du er på nett' });
        trackEvent('ferry_favorite_pending', { route: normalizedSlug });
        return;
      }
      throw err;
    }
  }, [applyCloudFavorites, cloudFavorites, cloudSambandFavorites, pendingQueue]);

  const removeCloudFavorite = useCallback(async (slugOrId: string) => {
    const anonUserId = await waitForAnonUserId();
    const normalizedSlug = normalizeSambandSlug(slugOrId);
    const fav = cloudFavorites.find(
      (f) => f.samband_slug === normalizedSlug || f.samband_slug === slugOrId || f.id === slugOrId,
    );
    if (!fav) return;

    if (fav.id.startsWith('legacy-')) {
      const next = cloudFavorites.filter((f) => f.id !== fav.id);
      applyCloudFavorites(next);
      trackEvent('ferry_favorite_removed', { route: fav.samband_slug });
      return;
    }

    try {
      await deleteFavorite(fav.samband_slug, anonUserId);
      const next = cloudFavorites.filter((f) => f.id !== fav.id);
      applyCloudFavorites(next);
      trackEvent('ferry_favorite_removed', { route: fav.samband_slug });
    } catch (err) {
      console.error('[FavoritesProvider] remove failed', err);
      throw err;
    }
  }, [applyCloudFavorites, cloudFavorites]);

  const updateCloudFavorite = useCallback(async (
    id: string,
    updates: Partial<Omit<FerryFavorite, 'id' | 'samband_slug' | 'created_at' | 'updated_at'>>,
  ) => {
    const anonUserId = await waitForAnonUserId();
    try {
      const data = await updateFavorite({ id, ...updates }, anonUserId);
      if (data?.favorite) {
        const next = cloudFavorites.map((f) => (f.id === id ? data.favorite : f));
        applyCloudFavorites(next);
      }
    } catch (err) {
      console.error('[FavoritesProvider] update failed', err);
      throw err;
    }
  }, [applyCloudFavorites, cloudFavorites]);

  const toggleCloudFavorite = useCallback(async (
    slug: string,
    options?: { isPro?: boolean; from?: string; to?: string },
  ) => {
    if (cloudIsFavorite(slug)) {
      await removeCloudFavorite(slug);
    } else {
      await addCloudFavorite(slug, undefined, options);
    }
  }, [cloudIsFavorite, addCloudFavorite, removeCloudFavorite]);

  const addLocalRoute = useCallback((slug: string, from: string, to: string) => {
    setLocalRoutes((prev) => {
      if (prev.some((f) => f.slug === slug)) return prev;
      const next = [{ slug, from, to, savedAt: new Date().toISOString() }, ...prev];
      writeLocalRoutes(next);
      trackEvent('favorite_route_set', { route: slug });
      return next;
    });
  }, []);

  const removeLocalRoute = useCallback((slug: string) => {
    const normalized = normalizeSambandSlug(slug);
    setLocalRoutes((prev) => {
      const next = prev.filter(
        (f) => normalizeSambandSlug(f.slug) !== normalized && f.slug !== slug,
      );
      writeLocalRoutes(next);
      trackEvent('favorite_route_removed', { route: slug });
      return next;
    });
  }, []);

  const toggleLocalRoute = useCallback((slug: string, from: string, to: string) => {
    if (localRoutes.some((f) => f.slug === slug)) {
      removeLocalRoute(slug);
    } else {
      addLocalRoute(slug, from, to);
    }
  }, [localRoutes, addLocalRoute, removeLocalRoute]);

  const removeSamband = useCallback(async (slug: string, _meta?: ToggleSambandMeta) => {
    const normalized = normalizeSambandSlug(slug);
    if (normalized.length === 0) return;

    // Always unified: remove from every store that contributed the visible row.
    // VITE_FAVORITES_UNIFIED does not gate remove (write-mode flag only).
    const cloudFav = findCloudFavoriteBySlug(cloudFavorites, normalized);
    const inLocal = hasLocalSamband(localRoutes, normalized);
    const inPending = pendingQueue.some((entry) => entry.samband_slug === normalized);
    const hadOnlyPending = inPending && !cloudFav && !inLocal;

    const removals: Promise<unknown>[] = [];
    if (cloudFav) {
      removals.push(removeCloudFavorite(cloudFav.samband_slug));
    }
    if (inLocal) {
      const localRoute = findLocalRoute(localRoutes, normalized);
      const rawSlug = localRoute?.slug ?? normalized;
      removals.push(Promise.resolve().then(() => removeLocalRoute(rawSlug)));
    }
    if (inPending) {
      removals.push(Promise.resolve().then(() => {
        const nextPending = dequeuePending(normalized);
        setPendingQueue(nextPending);
      }));
    }
    if (removals.length === 0) {
      console.warn('[FavoritesProvider] removeSamband: no matching favorite found', {
        slug,
        normalized,
        searchedStores: { cloud: Boolean(cloudFav), local: inLocal, pending: inPending },
      });
      return;
    }

    const results = await Promise.allSettled(removals);
    const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failures.length > 0) {
      console.error('[FavoritesProvider] removeSamband failed', failures.map((f) => f.reason));
      throw failures[0].reason;
    }

    if (hadOnlyPending) {
      toast({ title: 'Fjernet fra Mine ferger' });
    }
  }, [
    cloudFavorites,
    localRoutes,
    pendingQueue,
    removeCloudFavorite,
    removeLocalRoute,
  ]);

  const toggleSamband = useCallback(async (slug: string, meta?: ToggleSambandMeta): Promise<boolean> => {
    const normalized = normalizeSambandSlug(slug);
    if (normalized.length === 0) return false;

    const inPending = pendingQueue.some((entry) => entry.samband_slug === normalized);
    const pendingOnly = inPending && !cloudIsFavorite(normalized) && !hasLocalSamband(localRoutes, normalized);

    // VITE_FAVORITES_UNIFIED: unified read/remove on toggle-off (cloud + local + pending).
    if (unifiedEnabled) {
      if (isFavorite(normalized)) {
        await removeSamband(normalized, meta);
        return false;
      }

      await addCloudFavorite(normalized, meta?.customName, {
        isPro: meta?.isPro,
        from: meta?.from,
        to: meta?.to,
      });
      if (!hasLocalSamband(localRoutes, normalized)) {
        addLocalRoute(normalized, meta?.from ?? '', meta?.to ?? '');
      }
      return true;
    }

    // Flag off: pending dequeue uses unified remove (no cloud row exists).
    if (pendingOnly) {
      await removeSamband(normalized, meta);
      return false;
    }

    if (meta?.legacyWrite === 'local') {
      const localRoute = findLocalRoute(localRoutes, normalized);
      if (localRoute) {
        removeLocalRoute(localRoute.slug);
        return false;
      }
      addLocalRoute(normalized, meta?.from ?? '', meta?.to ?? '');
      return true;
    }

    if (cloudIsFavorite(normalized)) {
      await removeCloudFavorite(normalized);
      return false;
    }

    await addCloudFavorite(normalized, meta?.customName, {
      isPro: meta?.isPro,
      from: meta?.from,
      to: meta?.to,
    });
    return true;
  }, [
    unifiedEnabled,
    isFavorite,
    removeSamband,
    addCloudFavorite,
    localRoutes,
    pendingQueue,
    addLocalRoute,
    removeLocalRoute,
    removeCloudFavorite,
    cloudIsFavorite,
  ]);

  const refresh = useCallback(async () => {
    await refreshCloud();
  }, [refreshCloud]);

  const value = useMemo<FavoritesContextValue>(() => ({
    cloudFavorites,
    localRoutes,
    mergedList,
    isLoading,
    error,
    counts,
    showMyFerriesSection,
    refresh,
    isFavorite,
    toggleSamband,
    removeSamband,
    cloudIsFavorite,
    addCloudFavorite,
    removeCloudFavorite,
    updateCloudFavorite,
    toggleCloudFavorite,
    refreshCloud,
    localIsFavorite,
    addLocalRoute,
    removeLocalRoute,
    toggleLocalRoute,
  }), [
    cloudFavorites,
    localRoutes,
    mergedList,
    isLoading,
    error,
    counts,
    showMyFerriesSection,
    refresh,
    isFavorite,
    toggleSamband,
    removeSamband,
    cloudIsFavorite,
    addCloudFavorite,
    removeCloudFavorite,
    updateCloudFavorite,
    toggleCloudFavorite,
    refreshCloud,
    localIsFavorite,
    addLocalRoute,
    removeLocalRoute,
    toggleLocalRoute,
  ]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavoritesContext(): FavoritesContextValue {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavoritesContext must be used within FavoritesProvider');
  }
  return context;
}
